import React, { useEffect, useMemo } from 'react'
import injectSheet from 'react-jss'
import Bone from 'pobble-components/dist/bone'
import FormSelect from 'pobble-components/dist/form-elements/form-select'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { getYearGroups } from '../../stores/year-groups/year-groups.selectors'
import { fetchYearGroups } from '../../stores/year-groups/year-groups.action'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'

const styles = {
  ageContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '340px',
    minWidth: '100px',
    '& > *': {
      flex: '1'
    }
  },
  option: {
    '& > *': {
      padding: '10px 15px'
    }
  },
  ageDash: {
    flex: '0',
    margin: '0 16px'
  },
  bone: {
    borderRadius: '4px',
    height: '40px'
  },
  clear: {
    textDecoration: 'underline',
    marginLeft: '16px',
    '& > svg': {
      fontSize: '.7em',
      marginRight: '4px'
    }
  },
  hideClear: {
    opacity: '0',
    pointerEvents: 'none'
  }
}

const YearGroupsSelection = ({
  classes,
  selected,
  onChange,
  className,
  boneClassName,
  selectClassName,
  contrast,
  optionClassName,
  yearGroupsFilter = yearGroups => yearGroups.filter(group => group.order < 15)
}) => {
  const yearGroupsPull = useSelector(getYearGroups)
  const dispatch = useDispatch()

  useEffect(() => {
    !yearGroupsPull && dispatch(fetchYearGroups())
  }, []) // eslint-disable-line

  const yearGroups = useMemo(
    () => {
      return !yearGroupsPull ? null : yearGroupsFilter(yearGroupsPull)
    },
    [yearGroupsPull] // eslint-disable-line
  )

  const yearGroupsMap = useMemo(
    () => {
      if (!yearGroups) return null
      const result = {}
      yearGroups.forEach(yearGroup => (result[yearGroup.id] = yearGroup))
      return result
    },
    [yearGroups]
  )

  const range = useMemo(
    () => {
      if (!yearGroupsMap || !selected) return null

      if (!selected.length)
        return {
          from: yearGroups[0].order,
          to: yearGroups[yearGroups.length - 1].order
        }

      return {
        from: yearGroupsMap[selected[0]].order,
        to: yearGroupsMap[selected[selected.length - 1]].order
      }
    },
    [yearGroups, yearGroupsMap, selected]
  )

  const options = useMemo(
    () => {
      if (!yearGroups || !range) return null
      return {
        from: yearGroups.map(yearGroup => ({
          value: yearGroup.order,
          label: `Age ${yearGroup.from}`
        })),
        to: yearGroups
          .filter(yearGroup => yearGroup.order >= range.from)
          .map(yearGroup => ({
            value: yearGroup.order,
            label: `Age ${yearGroup.to}`
          }))
      }
    },
    [yearGroups, range]
  )

  return (
    <div className={classNames(classes.ageContainer, className)}>
      {!options && <Bone className={classNames(classes.bone, boneClassName)} />}
      {!!options && (
        <FormSelect
          contrast={contrast}
          selectClassName={selectClassName}
          value={range ? range.from : ''}
          onChange={e => {
            const value = parseInt(e.target.value)
            let selected = yearGroups.filter(yearGroup => {
              return yearGroup.order === value
            })

            if (selected.length === yearGroups.length) {
              selected = []
            }

            onChange({
              selected,
              selectedIds: selected.map(selected => selected.id)
            })
          }}>
          {options.from.map(option => (
            <option
              className={optionClassName}
              key={option.value}
              value={option.value}>
              {option.label}
            </option>
          ))}
        </FormSelect>
      )}
      <div className={classes.ageDash}>to</div>
      {!options && <Bone className={classNames(classes.bone, boneClassName)} />}
      {!!options && (
        <FormSelect
          contrast={contrast}
          selectClassName={selectClassName}
          value={range ? range.to : ''}
          onChange={e => {
            const value = parseInt(e.target.value)
            let selected = yearGroups.filter(yearGroup => {
              return yearGroup.order >= range.from && yearGroup.order <= value
            })

            if (selected.length === yearGroups.length) {
              selected = []
            }

            onChange({
              selected,
              selectedIds: selected.map(selected => selected.id)
            })
          }}>
          {options.to.map(option => (
            <option
              className={optionClassName}
              key={option.value}
              value={option.value}>
              {option.label}
            </option>
          ))}
        </FormSelect>
      )}
      <ButtonElement
        onClick={() => {
          onChange({
            selected: [],
            selectedIds: []
          })
        }}
        className={classNames(classes.clear, {
          [classes.hideClear]: !yearGroups || !selected.length
        })}>
        <FontAwesomeIcon icon={faTimes} />Clear
      </ButtonElement>
    </div>
  )
}

export default injectSheet(styles)(YearGroupsSelection)
